/**
 * azure-functions/shared/trainingDerivations.cjs
 *
 * CANONICAL COPY. `src/utils/trainingDerivations.js` re-exports this file, so
 * the browser, the engine and the backend (measurement base measures) read
 * one implementation — the controlOwnerResolution.cjs pattern. Do not fork it
 * again.
 *
 * THE source of truth for the three questions this product keeps answering in
 * more than one place:
 *
 *   1. which training modules exist,
 *   2. is this module assigned to this employee,
 *   3. is this employee's pass on it still current.
 *
 * Pure functions over tenant state — no React, no imports from `src/components`
 * — precisely so the engine (`src/engine/autoEvidence.js`,
 * `src/engine/evidenceDerivations.js`), the forecast
 * (`src/state/forecastControl.js`) and the Training UI can all import the same
 * answer instead of each carrying its own copy.
 *
 * WHY THIS FILE EXISTS. Seven surfaces answered question 2 and five of them
 * answered it direct-only — they could not see a module assigned through a
 * training programme, which is the normal way this product binds an employee to
 * a module. Two of those five decide whether a customer is told they are
 * compliant, so a programme-only tenant had `obl-security-awareness` reading
 * satisfied against training it had never looked at (GH #6457) and a set of
 * summary tiles reading 100% against passes that lapsed a year ago (GH #6462).
 * That is `TRYGGR_INTENT.md` §6.2's named failure, and CLAUDE.md's "Enter Once,
 * Know Everywhere" rule is explicit that if two surfaces can disagree about a
 * fact, the disagreement is the bug. One derivation, imported everywhere, is
 * the only shape that cannot drift back.
 *
 * Anything that needs to answer one of these questions imports from here. Do
 * not write a sixth `isAssigned()` switch.
 *
 * NOTE ON THE BACKEND. `azure-functions/employee-portal-data` and
 * `employee-portal-update` PREFER `trainingModules` over `trainingCourses`;
 * they do not union. That is deliberately not mirrored here — see
 * `trainingModulesOf` for why a preference is the one direction a read-both
 * accessor must never fail in. Do not "simplify" this file to match the
 * backend; if the two are ever brought into line, the backend moves to the
 * union, not this file to a preference.
 */

const { realEmployees } = require('./sampleRecords.cjs');

/**
 * The tr

'use strict';aining-module collection, read from the canonical key and the legacy
 * one.
 *
 * `state.trainingModules` is canonical (`initialState.js`,
 * `collectionRegistry.js`); `state.trainingCourses` is the name it had before
 * the rename. Tenants provisioned before the rename still hold their modules
 * under the legacy key and there is live data behind it, so every reader takes
 * both — reading only one is how `training-deadline-overdue` spent its whole
 * life returning "no active training courses defined (N/A)" on every tenant
 * created since (GH #6455). When both keys hold modules the result is their
 * UNION by id, not a preference — see the comment in the body for why that
 * distinction is load-bearing rather than tidiness.
 */
function trainingModulesOf(state) {
  const canonical = Array.isArray(state?.trainingModules) ? state.trainingModules : [];
  const legacy = Array.isArray(state?.trainingCourses) ? state.trainingCourses : [];
  if (legacy.length === 0) return canonical;
  if (canonical.length === 0) return legacy;
  // UNION, not a preference, and this is load-bearing. Preferring the canonical
  // list whenever it is non-empty would silently orphan any module still held
  // only under the legacy key — and orphaning modules SHRINKS the denominator
  // of both training checkers, which moves a compliance gate favourably. That
  // is the one direction a read-both accessor must never fail in.
  //
  // Today no write path produces a split like that, because TrainingTab copies
  // the legacy list forward on the first module it writes. That guarantee lives
  // in a component three files away, is untested, and breaks on `[]` rather
  // than `undefined` — which is exactly what initialState seeds. A union does
  // not depend on it holding.
  const byId = new Map();
  for (const m of legacy) if (m?.id) byId.set(m.id, m);
  for (const m of canonical) if (m?.id) byId.set(m.id, m); // canonical wins on collision
  return [...byId.values()];
}

/** The live (non-archived) training modules. */
function activeTrainingModulesOf(state) {
  return trainingModulesOf(state).filter((c) => c && !c.archivedAt);
}

/** The training-programme collection, always an array. */
function trainingProgramsOf(state) {
  return Array.isArray(state?.trainingPrograms) ? state.trainingPrograms : [];
}

/** The id of the evidence record a training pass writes. */
function trainingEvidenceId(courseId, employeeId) {
  return `evtc-${courseId}-${employeeId}`;
}

/**
 * Does one assignment target name this employee?
 *
 * The single copy of a switch that had been written out five times across the
 * engine, the modals and the detail views — which is how the surfaces drifted
 * apart in the first place. Deliberately says nothing about departed status:
 * whether a departed employee still counts is a population question the caller
 * answers, not a property of the target.
 */
function matchesAssignmentTarget(target, employee) {
  if (!target || !employee) return false;
  if (target.type === 'all') return true;
  if (target.type === 'department') return employee.department === target.value;
  if (target.type === 'specific') return employee.id === target.value;
  return false;
}

/**
 * Every training programme that carries this module and is still live.
 *
 * An archived programme assigns nobody — the employee portal already drops it
 * (`azure-functions/employee-portal-data`, `filter(p => !p.archivedAt …)`), so
 * every admin- and engine-side surface must drop it too, or the two disagree
 * again.
 *
 * `includeArchivedPrograms` exists for exactly one caller: a checker asking
 * whether an obligation it is about to report as satisfied became satisfied
 * because the population was WITHDRAWN. Answering that needs to see what was
 * withdrawn, which is the one question an archived programme is still evidence
 * for. Never use it to decide whether somebody is assigned today.
 */
function programsContainingModule(course, programs, { includeArchivedPrograms = false } = {}) {
  const courseId = course?.id;
  if (!courseId) return [];
  return (programs ?? []).filter(
    (p) =>
      p &&
      (includeArchivedPrograms || !p.archivedAt) &&
      (p.modules ?? []).some((m) => m?.moduleId === courseId)
  );
}

/**
 * Everyone a module is assigned to — directly, or by way of an assigned
 * training programme that contains it.
 *
 * `programs` is optional so a caller with no programme list keeps the old
 * direct-only contract, but nothing in the product should be calling it that
 * way: a direct-only answer is the defect this file exists to close.
 */
function resolveAssignedEmployeeIds(course, employees, programs = []) {
  const ids = new Set();
  // A sample person owes no training and is never counted as trained (GH #7870).
  const list = realEmployees(employees ?? []);
  const addAssignmentTargets = (assignments) => {
    for (const a of assignments ?? []) {
      // A departed employee owes no training — the settled answer everywhere
      // else in this module. Applying it here, rather than only to the `all`
      // and `department` branches as before, is what stops a `specific` target
      // naming a departed (or unlisted) person inflating `assignedCount` while
      // CourseDetail's own table — which intersects the same `employees` list —
      // renders no row for them: "3 ASSIGNED" above a two-row table.
      list
        .filter((e) => e.status !== 'departed' && matchesAssignmentTarget(a?.target, e))
        .forEach((e) => ids.add(e.id));
    }
  };

  addAssignmentTargets(course?.assignments);
  for (const p of programsContainingModule(course, programs)) {
    addAssignmentTargets(p.assignments);
  }
  return ids;
}

/**
 * Is this module assigned to this one employee — directly, or through a live
 * programme that contains it?
 *
 * The per-employee counterpart of `resolveAssignedEmployeeIds`. It answers for
 * a named person rather than a population, so it does NOT apply the departed
 * filter: a departed employee's own record still shows what they were assigned.
 *
 * NEVER BUILD A POPULATION COUNT BY CALLING THIS IN A LOOP. Because it counts
 * departed employees, a ratio assembled from it can read HIGHER than the truth
 * — one active employee at 0 of 1 plus one departed employee at 1 of 1 comes
 * out as 50% where the honest answer is 0%. `resolveAssignedEmployeeIds` is the
 * population function and applies the departed filter for exactly that reason;
 * use it, or `getCourseStats`, whenever the answer is a count.
 */
function isModuleAssignedToEmployee(course, employee, programs = []) {
  const hits = (assignments) =>
    (assignments ?? []).some((a) => matchesAssignmentTarget(a?.target, employee));
  if (hits(course?.assignments)) return true;
  return programsContainingModule(course, programs).some((p) => hits(p.assignments));
}

/**
 * Every deadline this employee is under for this module, direct and
 * programme-derived, earliest first.
 *
 * A deadline set on a programme assignment is a real deadline. Reading only
 * `course.assignments[].deadline` is why the Audit-Readiness Forecast could not
 * see a programme-only tenant's training decay at all (GH #6457) — and the
 * forecast's safety argument is directional: it may project decay it can see
 * and must never look cleaner than reality because it cannot see some.
 */
function deadlinesForEmployee(course, employee, programs = [], options = {}) {
  const from = (assignments) =>
    (assignments ?? [])
      .filter((a) => a?.deadline && matchesAssignmentTarget(a.target, employee))
      .map((a) => a.deadline);
  return [
    ...from(course?.assignments),
    ...programsContainingModule(course, programs, options).flatMap((p) => from(p.assignments)),
  ].sort();
}

/** The earliest deadline this employee is under for this module, or null. */
function earliestDeadlineForEmployee(course, employee, programs = []) {
  const deadlines = deadlinesForEmployee(course, employee, programs);
  return deadlines.length ? deadlines[0] : null;
}

/**
 * The earliest deadline for this pair that has already passed, or null.
 * Direct and programme-derived alike.
 */
function earliestPastDeadlineForEmployee(course, employee, programs = [], now = Date.now()) {
  const past = deadlinesForEmployee(course, employee, programs).filter(
    (d) => new Date(d).getTime() <= now
  );
  return past.length ? past[0] : null;
}

/**
 * The evidence record proving this employee's training on this module is
 * CURRENT — or null when it is not.
 *
 * "Did they pass once" and "are they covered now" are different questions, and
 * every compliance-facing surface must answer the second. A pass expires:
 * `employee-portal-update` stamps each one with
 * `validUntil = completedAt + course.validityDays` (365 by default) on the
 * `evtc-<courseId>-<employeeId>` record, and `employee-evidence-scheduler`
 * flips lapsed records to `status: 'expired'` every six hours.
 *
 * Deliberately reads the RECORD rather than recomputing the expiry from
 * `completedAt + validityDays`, so a display surface and the control gate agree
 * by construction rather than by coincidence. Counting `completion.passed`
 * alone shows "1 of 1 courses completed" for training that lapsed a year ago
 * while the gate reports the control unmet.
 */
function currentTrainingEvidence(course, employee, evidenceRecords, now = Date.now()) {
  if (!course?.id || !employee?.id) return null;
  const id = trainingEvidenceId(course.id, employee.id);
  const rec = (evidenceRecords ?? []).find((r) => r?.id === id);
  if (!rec || rec.status !== 'verified') return null;
  // GH #6477 — a pass against superseded content is not current, whatever its
  // expiry says. Checked before the expiry so the lapse reason can be specific.
  if (!passIsForCurrentContent(course, rec)) return null;
  const expiry = effectiveValidUntil(course, rec);
  if (!expiry) return null; // GH #6501 — no expiry derivable at all
  if (!isFutureInstant(expiry, now)) return null;
  return rec;
}

/** Days a pass is good for when the module does not say. Matches
 *  `employee-portal-update`'s own `course.validityDays ?? 365`. */
const DEFAULT_TRAINING_VALIDITY_DAYS = 365;

/**
 * Has this module changed materially enough since the pass that the pass no
 * longer evidences the current content?
 *
 * GH #6477, founder decision A. `employee-portal-data` already told the
 * EMPLOYEE to retake a module whose `version` had moved on, while every admin
 * surface and the control gate ignored it — so the employee was told to retake
 * training the product simultaneously reported as satisfied. "Enter Once, Know
 * Everywhere": if two surfaces can disagree about a fact, the disagreement is
 * the bug.
 *
 * The fix is NOT to wire `version` into the gate, which is why this was never
 * done. `TrainingTab.jsx` increments `version` on EVERY save, so a typo fix
 * would retroactively un-train everyone who ever passed the module — across
 * every tenant, with no warning. That is the same un-train-a-workforce hazard
 * GH #6501 refused, arriving from a different direction.
 *
 * So materiality is its own field. `majorVersion` moves only when an admin
 * says the content changed, and a pass is stale only when it was taken against
 * an EARLIER major.
 *
 * BACKWARD COMPATIBLE BY CONSTRUCTION. Both sides default to 1, so every
 * existing module and every existing record compare 1 < 1 → false → still
 * current. Nobody is un-trained by this shipping; the field only starts
 * biting once an admin deliberately bumps it.
 */
function passIsForCurrentContent(course, rec) {
  const moduleMajor = course?.majorVersion ?? 1;
  const passMajor = rec?.courseMajorVersion ?? 1;
  return passMajor >= moduleMajor;
}

/**
 * When this pass expires — the stamped `validUntil`, or one derived from when
 * the record was verified.
 *
 * GH #6501. `validUntil` was treated as optional: absent meant NEVER EXPIRES,
 * on every surface at once. `employee-portal-update` stamps it on the current
 * quiz path, so the exposed population is records that predate that stamping,
 * or arrived by import or by hand — unknown size, and every one of them read
 * as covered forever. §5 says the direction an evaluator must not fail in is
 * the reassuring one, and "no expiry recorded" resolving to "never expires" is
 * exactly that failure.
 *
 * DERIVING rather than defaulting to not-current is what makes this safe to
 * ship in one deploy. Flipping absent → not-current on its own would un-train,
 * without warning, every employee whose record predates the stamping — on some
 * tenants the entire workforce, most of whom genuinely are trained. Deriving
 * from the verification date reconstructs the expiry the record should always
 * have carried: a pass verified 14 months ago on a 365-day module lapses,
 * which is true, and one verified last week does not.
 *
 * `null` — genuinely not current — is reserved for a record carrying no usable
 * anchor at all, which is malformed data rather than an un-stamped pass.
 *
 * The ANCHOR is the record's own lifecycle, not the completion record's
 * `completedAt`: `trainingCompletions[]` is a different array that a legacy or
 * imported evidence record may have no counterpart in. `reviewedAt` (verified)
 * is preferred over `submittedAt` (handed in) over `createdAt` (row made).
 *
 * DUPLICATED in `azure-functions/shared/evidence/scheduler.js`, which predates
 * this file living in `azure-functions/` (nothing there may import from
 * `src/`). The sweeper must apply the identical rule or the admin app and the
 * sweeper disagree again — the exact split this module exists to close.
 * Change them together (training-validity-parity.test.js pins it); the
 * sweeper can now import this file instead.
 */
function effectiveValidUntil(course, rec) {
  if (rec?.validUntil) return rec.validUntil;
  const anchor = rec?.reviewedAt || rec?.submittedAt || rec?.createdAt;
  if (!anchor) return null;
  const t = new Date(anchor).getTime();
  if (!Number.isFinite(t)) return null;
  const days = course?.validityDays ?? DEFAULT_TRAINING_VALIDITY_DAYS;
  return new Date(t + days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Is this timestamp a real instant still in the future?
 *
 * `new Date('garbage').getTime()` is NaN, and every comparison against NaN is
 * false — so a plain `validUntil <= now` test reads an UNPARSEABLE expiry as
 * still valid. In a predicate whose whole premise is "a pass expires", the
 * corrupt case must resolve to not-current, not to covered-forever.
 *
 * An ABSENT `validUntil` used to be treated as non-expiring here AND in
 * `azure-functions/shared/evidence/scheduler.js`, so the two agreed — on the
 * answer that told the customer they were covered. GH #6501, founder decision
 * A: both sides now DERIVE the expiry instead (see `effectiveValidUntil`), and
 * the sweeper persists what it derives. They still agree; they agree on the
 * true answer.
 */
function isFutureInstant(value, now) {
  const t = new Date(value).getTime();
  return Number.isFinite(t) && t > now;
}

/**
 * Why an employee is not currently covered for a module — so a surface can say
 * "Retake due — expired 4 Aug 2026" rather than a bare "retake due" the admin
 * then has to go to the Evidence Records tab to explain.
 *
 * Returns null when they ARE covered. The caller formats the date; this stays
 * free of presentation so the engine can use it too.
 *
 * @returns {{ kind: 'missing'|'expired'|'unverified'|'superseded'|'no-expiry-recorded', validUntil?: string, status?: string } | null}
 */
function trainingLapseReason(course, employee, evidenceRecords, now = Date.now()) {
  if (!course?.id || !employee?.id) return { kind: 'missing' };
  const id = trainingEvidenceId(course.id, employee.id);
  const rec = (evidenceRecords ?? []).find((r) => r?.id === id);
  if (!rec) return { kind: 'missing' };
  if (rec.status !== 'verified') return { kind: 'unverified', status: rec.status };
  // GH #6501. This MUST read the same expiry `currentTrainingEvidence` reads.
  // It briefly did not, and the result was the split this module exists to
  // prevent, inside this file: the derivation said not-covered while the
  // reason said covered, so the screen printed "no verified evidence record"
  // beside a record that is verified and visible in the Evidence Records tab.
  if (!passIsForCurrentContent(course, rec)) {
    return {
      kind: 'superseded',
      passMajorVersion: rec.courseMajorVersion ?? 1,
      moduleMajorVersion: course.majorVersion ?? 1,
    };
  }
  const expiry = effectiveValidUntil(course, rec);
  if (!expiry) return { kind: 'no-expiry-recorded' };
  if (!isFutureInstant(expiry, now)) {
    return { kind: 'expired', validUntil: expiry, derived: !rec.validUntil };
  }
  return null;
}

module.exports = {
  trainingModulesOf,
  activeTrainingModulesOf,
  trainingProgramsOf,
  trainingEvidenceId,
  matchesAssignmentTarget,
  programsContainingModule,
  resolveAssignedEmployeeIds,
  isModuleAssignedToEmployee,
  deadlinesForEmployee,
  earliestDeadlineForEmployee,
  earliestPastDeadlineForEmployee,
  currentTrainingEvidence,
  DEFAULT_TRAINING_VALIDITY_DAYS,
  passIsForCurrentContent,
  effectiveValidUntil,
  trainingLapseReason,
};
